import reportdeliverynoteService from '../../../services/reportdeliverynoteService';
import filterService from '../../../services/filterService';

import {
    setReportdeliverynoteLoading,
    setReportdeliverynoteError,
    setReportdeliverynoteList,
    setReportdeliverynoteCurrent,
    setReportdeliverynotePagination,
    setReportdeliverynoteSort,
    setReportdeliverynoteFilters,
    setReportdeliverynoteDefaultFilter,
    setReportdeliverynoteSettings,
    clearReportdeliverynoteCurrent,
    clearReportdeliverynoteError,
    setReportdeliverynoteFilterLoading,
    changeReportdeliverynoteFilterCondition,
    addReportdeliverynoteFilterCondition,
    updateReportdeliverynoteFilterConditionRule,
    updateReportdeliverynoteFilterConditionValue,
    removeReportdeliverynoteFilterCondition,
    setReportdeliverynoteSelected,
    setReportdeliverynoteDownloading,
    resetReportdeliverynoteFiltersAndPagination,

    setReportdeliverynoteClassicFilters
} from './reportdeliverynoteSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportdeliverynote.isSettingsInitialized) {
            dispatch(setReportdeliverynoteLoading(true));
            const response = await reportdeliverynoteService.getSettings();

            // Set the settings
            dispatch(setReportdeliverynoteSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportdeliverynoteClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportdeliverynoteDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportdeliverynoteError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportdeliverynoteLoading(false));
    }
};

export const fetchReportdeliverynotes = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportdeliverynote;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportdeliverynoteLoading(true));
        const response = await reportdeliverynoteService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportdeliverynoteList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReportdeliverynotes:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportdeliverynoteError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportdeliverynoteLoading(false));
    }
};

export const fetchReportdeliverynote = (id) => async (dispatch) => {
    try {
        dispatch(setReportdeliverynoteLoading(true));
        const response = await reportdeliverynoteService.getOne(id);
        dispatch(setReportdeliverynoteCurrent(response.data));
    } catch (error) {
        dispatch(setReportdeliverynoteError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportdeliverynoteLoading(false));
    }
};

export const createReportdeliverynote = (data) => async (dispatch) => {
    try {
        const response = await reportdeliverynoteService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportdeliverynote = (id, data) => async (dispatch) => {
    try {
        const response = await reportdeliverynoteService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportdeliverynote = (id) => async (dispatch) => {
    try {
        dispatch(setReportdeliverynoteLoading(true));
        const response = await reportdeliverynoteService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportdeliverynoteError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportdeliverynoteLoading(false));
    }
};

export const downloadReportdeliverynote = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportdeliverynote;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportdeliverynoteDownloading(true));
        const response = await reportdeliverynoteService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportdeliverynoteDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportdeliverynotes:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportdeliverynoteError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportdeliverynoteDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportdeliverynotePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportdeliverynoteSort(sort));
};

export const searchReportdeliverynotes = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportdeliverynote.filters;
    dispatch(setReportdeliverynoteFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportdeliverynoteDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportdeliverynoteFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportdeliverynoteFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportdeliverynoteFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportdeliverynoteFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportdeliverynoteFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportdeliverynoteFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportdeliverynote.settings;
            dispatch(setReportdeliverynoteSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportdeliverynoteFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportdeliverynoteFilterLoading(false));
        dispatch(setReportdeliverynoteError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportdeliverynoteSelected(selectedRows));
    };
};

export const clearCurrentReportdeliverynote = () => {
    return (dispatch) => {
        dispatch(clearReportdeliverynoteCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportdeliverynoteFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportdeliverynote.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportdeliverynoteClassicFilters(updatedClassicFilters));
};